<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    append-to="body"
    :header="$t('ProjectManagement.decisions.logTitle')"
    class="w-full max-w-5xl"
    data-testid="overview-decision-log-dialog"
    @hide="handleHide"
  >
    <div class="flex flex-col gap-4">
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('ProjectManagement.decisions.create')"
          icon="pi pi-plus"
          class="btn-primary-accent"
          unstyled
          data-testid="decision-log-create"
          @click="openCreateDialog"
        />
        <Button
          :label="$t('ProjectManagement.decisions.refresh')"
          icon="pi pi-refresh"
          severity="secondary"
          :loading="loading"
          @click="refresh"
        />
      </div>

      <Message v-if="error" severity="error" :closable="false">
        {{ error }}
      </Message>

      <div v-if="loading" class="flex items-center justify-center py-12 text-[var(--color-disabled)]">
        <i class="pi pi-spin pi-spinner mr-2" />
        {{ $t('ProjectManagement.decisions.loading') }}
      </div>

      <div
        v-else-if="isEmpty"
        class="text-center py-8 text-[var(--color-disabled)]"
        data-testid="decision-log-empty"
      >
        <div class="font-medium text-[var(--color-dark)]">{{ $t('ProjectManagement.decisions.emptyTitle') }}</div>
        <div class="text-sm mt-1">{{ $t('ProjectManagement.decisions.emptyHint') }}</div>
      </div>

      <DataTable v-else :value="decisions" data-key="id" striped-rows>
        <Column field="title" :header="$t('ProjectManagement.decisions.columns.title')" />
        <Column :header="$t('ProjectManagement.decisions.columns.status')">
          <template #body="{ data }">
            <Tag
              :value="$t(`ProjectManagement.decisions.status.${data.status}`)"
              :severity="statusSeverity(data.status)"
            />
          </template>
        </Column>
        <Column :header="$t('ProjectManagement.decisions.columns.owner')">
          <template #body="{ data }">
            {{ userLabel(data.decisionOwnerUserId) }}
          </template>
        </Column>
        <Column field="dueDate" :header="$t('ProjectManagement.decisions.columns.dueDate')">
          <template #body="{ data }">
            {{ formatDate(data.dueDate) }}
          </template>
        </Column>
        <Column :header="$t('ProjectManagement.decisions.columns.escalation')">
          <template #body="{ data }">
            {{ data.escalationLevel ?? 0 }}
          </template>
        </Column>
        <Column :header="$t('ProjectManagement.decisions.columns.actions')">
          <template #body="{ data }">
            <div class="flex gap-1 flex-wrap">
              <Button
                v-if="isEditable(data.status)"
                icon="pi pi-pencil"
                severity="secondary"
                text
                rounded
                @click="openEditDialog(data)"
              />
              <Button
                v-if="data.status === 'open'"
                icon="pi pi-file-edit"
                severity="info"
                text
                rounded
                :title="$t('ProjectManagement.decisions.actions.markPrepared')"
                @click="markStatus(data, 'prepared')"
              />
              <Button
                v-if="isPending(data.status)"
                icon="pi pi-check"
                severity="secondary"
                text
                rounded
                class="text-[var(--color-orange)]"
                :title="$t('ProjectManagement.decisions.actions.markDecided')"
                @click="markStatus(data, 'decided')"
              />
              <Button
                v-if="isPending(data.status)"
                icon="pi pi-arrow-up"
                severity="warn"
                text
                rounded
                :title="$t('ProjectManagement.decisions.actions.escalate')"
                @click="escalate(data)"
              />
              <Button
                v-if="isEditable(data.status)"
                icon="pi pi-trash"
                severity="danger"
                text
                rounded
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog
      v-model:visible="formDialogVisible"
      modal
      append-to="body"
      :header="formMode === 'create'
        ? $t('ProjectManagement.decisions.createTitle')
        : $t('ProjectManagement.decisions.editTitle')"
      class="w-full max-w-2xl"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.title') }}</label>
          <InputText v-model="form.title" class="w-full" :invalid="!!formErrors.title" />
          <small v-if="formErrors.title" class="text-[var(--color-error)]">{{ formErrors.title }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.decisionOwnerUserId') }}</label>
          <UserSelectInput
            v-model="form.decisionOwnerUserId"
            :users="participantUsers"
            class="w-full"
            :invalid="!!formErrors.decisionOwnerUserId"
          />
          <small v-if="formErrors.decisionOwnerUserId" class="text-[var(--color-error)]">
            {{ formErrors.decisionOwnerUserId }}
          </small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.preparedByUserId') }}</label>
          <UserSelectInput
            v-model="form.preparedByUserId"
            :users="participantUsers"
            class="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.dueDate') }}</label>
          <InputText v-model="form.dueDate" type="date" class="w-full" :invalid="!!formErrors.dueDate" />
          <small v-if="formErrors.dueDate" class="text-[var(--color-error)]">{{ formErrors.dueDate }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.basis') }}</label>
          <Textarea v-model="form.basis" class="w-full" rows="2" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="font-medium">{{ $t('ProjectManagement.decisions.form.impactIfDelayed') }}</label>
          <Textarea v-model="form.impactIfDelayed" class="w-full" rows="2" />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="flex flex-col gap-1">
            <label class="font-medium text-sm">{{ $t('ProjectManagement.decisions.form.commitmentId') }}</label>
            <InputText v-model="form.commitmentId" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="font-medium text-sm">{{ $t('ProjectManagement.decisions.form.constraintId') }}</label>
            <InputText v-model="form.constraintId" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="font-medium text-sm">{{ $t('ProjectManagement.decisions.form.milestoneId') }}</label>
            <Select
              v-model="form.milestoneId"
              :options="milestoneOptions"
              option-label="label"
              option-value="value"
              show-clear
              class="w-full"
              :placeholder="$t('ProjectManagement.decisions.form.milestonePlaceholder')"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <Button :label="$t('ProjectManagement.cancel')" severity="secondary" text @click="closeFormDialog" />
        <Button
          :label="formMode === 'create'
            ? $t('ProjectManagement.decisions.create')
            : $t('ProjectManagement.decisions.save')"
          :loading="saving"
          class="btn-primary-accent"
          unstyled
          @click="submitForm"
        />
      </template>
    </Dialog>
  </Dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import UserSelectInput from '@/components/global/UserSelectInput.vue';
import { resolveCurrentUserId } from '@/utils/resolveCurrentUserId';

const EMPTY_FORM = () => ({
  title: '',
  decisionOwnerUserId: '',
  preparedByUserId: '',
  dueDate: '',
  basis: '',
  impactIfDelayed: '',
  commitmentId: '',
  constraintId: '',
  milestoneId: null,
});

function toDateInputValue(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'OverviewDecisionLogDialog',
  components: {
    Button,
    DataTable,
    Column,
    Message,
    Dialog,
    InputText,
    Textarea,
    Select,
    Tag,
    UserSelectInput,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    openCreateOnShow: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible'],
  inject: {
    participantUsersProvider: { from: 'participantUsers', default: () => () => [] },
  },
  setup() {
    return {
      confirm: useConfirm(),
      toast: useToast(),
    };
  },
  data() {
    return {
      formDialogVisible: false,
      formMode: 'create',
      editingId: null,
      form: EMPTY_FORM(),
      formErrors: {},
      saving: false,
    };
  },
  computed: {
    ...mapGetters('pmDecision', {
      decisions: 'decisions',
      loading: 'loading',
      error: 'error',
      isEmpty: 'isEmpty',
    }),
    ...mapGetters('pmMilestone', {
      milestoneOptions: 'milestoneOptions',
    }),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
    }),
    ...mapGetters(['getUser']),
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    currentUserId() {
      return resolveCurrentUserId(this.getUser);
    },
    participantUsers() {
      return this.participantUsersProvider() || [];
    },
  },
  watch: {
    visible(isOpen) {
      if (isOpen) {
        this.refresh();
        if (this.openCreateOnShow) {
          this.$nextTick(() => this.openCreateDialog());
        }
      }
    },
  },
  methods: {
    ...mapActions('pmDecision', [
      'setProjectContext',
      'fetchDecisions',
      'createDecision',
      'updateDecision',
      'updateDecisionStatus',
      'escalateDecision',
      'deleteDecision',
    ]),
    ...mapActions('pmCommitment', ['fetchUserProjectsOverview']),

    handleHide() {
      this.formDialogVisible = false;
    },

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString();
    },

    userLabel(userId) {
      if (!userId) return '—';
      const user = this.participantUsers.find((item) => String(item.id) === String(userId));
      if (!user) return userId;
      return user.name || user.email || userId;
    },

    isPending(status) {
      return status === 'open' || status === 'prepared' || status === 'escalated';
    },

    isEditable(status) {
      return this.isPending(status);
    },

    statusSeverity(status) {
      if (status === 'decided') return 'success';
      if (status === 'escalated') return 'danger';
      if (status === 'prepared') return 'info';
      return 'warn';
    },

    async refresh() {
      if (!this.selectedProjectId) return;
      try {
        await this.setProjectContext(this.selectedProjectId);
      } catch {
        // handled in store
      }
    },

    async refreshOverview() {
      try {
        await this.fetchUserProjectsOverview();
      } catch {
        // overview errors surfaced on overview page
      }
    },

    openCreateDialog() {
      this.formMode = 'create';
      this.editingId = null;
      this.form = {
        ...EMPTY_FORM(),
        decisionOwnerUserId: this.currentUserId || '',
      };
      this.formErrors = {};
      this.formDialogVisible = true;
    },

    openEditDialog(decision) {
      this.formMode = 'edit';
      this.editingId = decision.id;
      this.form = {
        title: decision.title || '',
        decisionOwnerUserId: decision.decisionOwnerUserId || '',
        preparedByUserId: decision.preparedByUserId || '',
        dueDate: toDateInputValue(decision.dueDate),
        basis: decision.basis || '',
        impactIfDelayed: decision.impactIfDelayed || '',
        commitmentId: decision.commitmentId || '',
        constraintId: decision.constraintId || '',
        milestoneId: decision.milestoneId || null,
      };
      this.formErrors = {};
      this.formDialogVisible = true;
    },

    closeFormDialog() {
      this.formDialogVisible = false;
      this.saving = false;
      this.formErrors = {};
    },

    validateForm() {
      const errors = {};
      if (!String(this.form.title || '').trim()) {
        errors.title = this.$t('ProjectManagement.decisions.validation.titleRequired');
      }
      if (!String(this.form.decisionOwnerUserId || '').trim()) {
        errors.decisionOwnerUserId = this.$t('ProjectManagement.decisions.validation.ownerRequired');
      }
      if (!String(this.form.dueDate || '').trim()) {
        errors.dueDate = this.$t('ProjectManagement.decisions.validation.dueDateRequired');
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },

    buildPayload() {
      return {
        title: this.form.title.trim(),
        decisionOwnerUserId: String(this.form.decisionOwnerUserId),
        preparedByUserId: this.form.preparedByUserId ? String(this.form.preparedByUserId) : undefined,
        dueDate: new Date(this.form.dueDate).toISOString(),
        basis: this.form.basis?.trim() || undefined,
        impactIfDelayed: this.form.impactIfDelayed?.trim() || undefined,
        commitmentId: this.form.commitmentId?.trim() || undefined,
        constraintId: this.form.constraintId?.trim() || undefined,
        milestoneId: this.form.milestoneId || undefined,
      };
    },

    async submitForm() {
      if (!this.validateForm()) return;
      this.saving = true;
      const payload = this.buildPayload();
      try {
        if (this.formMode === 'create') {
          await this.createDecision(payload);
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.decisions.toast.created'),
            life: 3000,
          });
        } else {
          await this.updateDecision({ id: this.editingId, payload });
          this.toast.add({
            severity: 'success',
            summary: this.$t('ProjectManagement.decisions.toast.updated'),
            life: 3000,
          });
        }
        this.closeFormDialog();
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },

    async markStatus(decision, status) {
      try {
        await this.updateDecisionStatus({ id: decision.id, status });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.decisions.toast.statusUpdated'),
          life: 3000,
        });
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      }
    },

    async escalate(decision) {
      try {
        await this.escalateDecision(decision.id);
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.decisions.toast.escalated'),
          life: 3000,
        });
        await this.refreshOverview();
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.decisions.toast.error'),
          life: 4000,
        });
      }
    },

    confirmDelete(decision) {
      this.confirm.require({
        message: this.$t('ProjectManagement.decisions.deleteConfirm', { name: decision.title }),
        header: this.$t('ProjectManagement.decisions.deleteTitle'),
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: async () => {
          try {
            await this.deleteDecision(decision.id);
            this.toast.add({
              severity: 'success',
              summary: this.$t('ProjectManagement.decisions.toast.deleted'),
              life: 3000,
            });
            await this.refreshOverview();
          } catch {
            this.toast.add({
              severity: 'error',
              summary: this.$t('ProjectManagement.decisions.toast.error'),
              life: 4000,
            });
          }
        },
      });
    },
  },
};
</script>
